import React from "react";
import Modal from "@cloudscape-design/components/modal";
import Tabs from "@cloudscape-design/components/tabs";
import Alert from "@cloudscape-design/components/alert";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Select from "@cloudscape-design/components/select";
import Multiselect from "@cloudscape-design/components/multiselect";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Container from "@cloudscape-design/components/container";
import Grid from "@cloudscape-design/components/grid";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Toggle from "@cloudscape-design/components/toggle";
import Spinner from "@cloudscape-design/components/spinner";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { depositLadderFormStore, createDepositLadderForm, getClaimPeriodHours } from "../stores/depositLadder";

const typeOptions = ["local", "international", "mixed"].map((value) => ({ value, label: value }));
const modeOptions = [
	{ value: "all", label: "All" },
	{ value: "affiliate", label: "Affiliate" },
];
const periodOptions = [
	{ value: "hours", label: "Hours" },
	{ value: "days", label: "Days" },
];
const rewardTypeOptions = [
	{ value: "deposit", label: "Deposit bonus" },
	{ value: "freespins", label: "Free Spins" },
	{ value: "both", label: "Deposit bonus + Free Spins" },
];
const MAX_SIGNED_INT = 2147483647;
const MIN_FS_PART_DELAY_HOURS = 0;
const MAX_FS_PART_DELAY_HOURS = 8760;

const createFsPart = (isFirst = false) => ({
	_rowId: crypto.randomUUID(),
	spinsCount: "",
	delayHours: isFirst ? "0" : "",
});

const TextField = ({ label, field, typeField, form, patch }) => (
	<Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
		<FormField label={label}>
			<Input value={form[field]} onChange={({ detail }) => patch({ [field]: detail.value })} />
		</FormField>
		<FormField label={`${label} type`}>
			<Select selectedOption={typeOptions.find((item) => item.value === form[typeField])} options={typeOptions} onChange={({ detail }) => patch({ [typeField]: detail.selectedOption.value })} />
		</FormField>
	</Grid>
);

export default function DepositLadderFormModal({ Interface }) {
	const form = useRecoilValue(depositLadderFormStore);
	const setForm = useSetRecoilState(depositLadderFormStore);
	const [countryOptions, setCountryOptions] = React.useState([]);
	const [countriesLoading, setCountriesLoading] = React.useState(false);
	const [countriesError, setCountriesError] = React.useState(false);
	const [countriesLoaded, setCountriesLoaded] = React.useState(false);
	const [languageOptions, setLanguageOptions] = React.useState([]);
	const [languagesLoading, setLanguagesLoading] = React.useState(false);
	const [languagesError, setLanguagesError] = React.useState(false);
	const [languagesLoaded, setLanguagesLoaded] = React.useState(false);
	const [gameOptions, setGameOptions] = React.useState([]);
	const [gamesLoading, setGamesLoading] = React.useState(false);
	const [gamesError, setGamesError] = React.useState(false);
	const [currencyOptions, setCurrencyOptions] = React.useState([]);
	const [currenciesLoading, setCurrenciesLoading] = React.useState(false);
	const [currenciesError, setCurrenciesError] = React.useState(false);
	const [currenciesLoaded, setCurrenciesLoaded] = React.useState(false);
	const [fieldErrors, setFieldErrors] = React.useState({});
	const [activeTabId, setActiveTabId] = React.useState("general");
	const countriesRequestId = React.useRef(0);
	const languagesRequestId = React.useRef(0);
	const gamesRequestId = React.useRef(0);
	const currenciesRequestId = React.useRef(0);
	const stepsReadOnly = form.formMode === "edit";
	const countriesReady = countriesLoaded && !countriesLoading && !countriesError;
	const languagesReady = languagesLoaded && !languagesLoading && !languagesError;
	const currenciesReady = currenciesLoaded && !currenciesLoading && !currenciesError;
	const formHasFreespins = form.steps.some((step) => step.rewardType === "freespins" || step.rewardType === "both");
	const patch = (value) => setForm((current) => ({ ...current, ...value }));
	const close = () => {
		setCountriesLoaded(false);
		setLanguagesLoaded(false);
		setCurrenciesLoaded(false);
		setFieldErrors({});
		setActiveTabId("general");
		setForm(createDepositLadderForm());
	};
	const loadCountries = React.useCallback(() => {
		const requestId = ++countriesRequestId.current;
		setCountriesLoading(true);
		setCountriesError(false);
		setCountriesLoaded(false);

		Interface.request("get", { class: "data", module: "depositLadder", method: "listAllowedCountries" }, {})
			.then((response) => {
				if (requestId !== countriesRequestId.current) return;
				if (!Array.isArray(response?.items)) throw new Error("invalid_countries_response");

				const options = response.items.map((item) => {
					if (!item || typeof item.code !== "string" || typeof item.label !== "string") throw new Error("invalid_countries_response");
					return { value: item.code, label: item.label };
				});

				setCountryOptions(options);
				setCountriesLoading(false);
				setCountriesLoaded(true);
			})
			.catch(() => {
				if (requestId !== countriesRequestId.current) return;
				setCountryOptions([]);
				setCountriesLoading(false);
				setCountriesError(true);
				setCountriesLoaded(false);
			});
	}, [Interface]);
	const loadLanguages = React.useCallback(() => {
		const requestId = ++languagesRequestId.current;
		setLanguagesLoading(true);
		setLanguagesError(false);
		setLanguagesLoaded(false);

		Interface.request("get", { class: "data", module: "depositLadder", method: "listBrowserLanguages" }, {})
			.then((response) => {
				if (requestId !== languagesRequestId.current) return;
				if (!Array.isArray(response?.items) || response.items.length === 0) throw new Error("invalid_languages_response");
				const options = response.items.map((item) => {
					if (!item || typeof item.code !== "string" || !/^[a-z]{2}$/.test(item.code) || typeof item.label !== "string") throw new Error("invalid_languages_response");
					return { value: item.code, label: item.label };
				});
				setLanguageOptions(options);
				setLanguagesLoading(false);
				setLanguagesLoaded(true);
			})
			.catch(() => {
				if (requestId !== languagesRequestId.current) return;
				setLanguageOptions([]);
				setLanguagesLoading(false);
				setLanguagesError(true);
				setLanguagesLoaded(false);
			});
	}, [Interface]);
	const loadGames = React.useCallback(() => {
		const requestId = ++gamesRequestId.current;
		setGamesLoading(true);
		setGamesError(false);

		Interface.request("get", { class: "data", module: "depositLadder", method: "listEligibleFreespinsGames" }, {})
			.then((response) => {
				if (requestId !== gamesRequestId.current) return;
				if (!Array.isArray(response?.items)) throw new Error("invalid_games_response");

				const options = response.items.map((item) => {
					if (!item || !Number.isInteger(item.gameId) || item.gameId < 1 || item.gameId > MAX_SIGNED_INT || (item.gameName !== null && typeof item.gameName !== "string")) {
						throw new Error("invalid_games_response");
					}
					return { value: String(item.gameId), label: item.gameName || `Game #${item.gameId}`, description: `ID: ${item.gameId}` };
				});

				setGameOptions(options);
				setGamesLoading(false);
			})
			.catch(() => {
				if (requestId !== gamesRequestId.current) return;
				setGameOptions([]);
				setGamesLoading(false);
				setGamesError(true);
			});
	}, [Interface]);
	const loadCurrencies = React.useCallback(() => {
		const requestId = ++currenciesRequestId.current;
		setCurrenciesLoading(true);
		setCurrenciesError(false);
		setCurrenciesLoaded(false);

		Interface.request("get", { class: "data", module: "depositLadder", method: "listEligibleFreespinsCurrencies" }, {})
			.then((response) => {
				if (requestId !== currenciesRequestId.current) return;
				if (!Array.isArray(response?.items)) throw new Error("invalid_currencies_response");

				const options = response.items.map((item) => {
					if (!item || typeof item.code !== "string" || !/^[A-Z]{3}$/.test(item.code)) throw new Error("invalid_currencies_response");
					return { value: item.code, label: item.code };
				});

				setCurrencyOptions(options);
				setCurrenciesLoading(false);
				setCurrenciesLoaded(true);
			})
			.catch(() => {
				if (requestId !== currenciesRequestId.current) return;
				setCurrencyOptions([]);
				setCurrenciesLoading(false);
				setCurrenciesError(true);
				setCurrenciesLoaded(false);
			});
	}, [Interface]);

	React.useEffect(() => {
		if (!form.show) return undefined;

		setFieldErrors({});
		setActiveTabId("general");
		loadCountries();
		loadLanguages();
		loadGames();
		loadCurrencies();

		return () => {
			countriesRequestId.current += 1;
			languagesRequestId.current += 1;
			gamesRequestId.current += 1;
			currenciesRequestId.current += 1;
		};
	}, [form.show, loadCountries, loadLanguages, loadGames, loadCurrencies]);

	const emptyStep = (depositNumber) => ({
		depositNumber,
		rewardType: "deposit",
		bonusPercent: "",
		minDeposit: "",
		maxBonusAmount: "",
		wager: "",
		cap: "",
		fsGameId: null,
		fsBetAmount: null,
		fsCurrency: null,
		fsExpirationDays: null,
		fsParts: null,
	});
	const numberField = (label, field) => (
		<FormField label={label}>
			<Input type="number" value={String(form[field])} onChange={({ detail }) => patch({ [field]: detail.value })} />
		</FormField>
	);

	const validateForm = () => {
		const errors = {};

		["allowedBrowserLanguages", "excludedBrowserLanguages"].forEach((field) => {
			const codes = form[field];
			if (!Array.isArray(codes) || codes.some((code) => !languageOptions.some((option) => option.value === code)) || new Set(codes).size !== codes.length) {
				errors[field] = "Select valid browser languages without duplicates.";
			}
		});
		if (Array.isArray(form.allowedBrowserLanguages) && Array.isArray(form.excludedBrowserLanguages) && form.allowedBrowserLanguages.some((code) => form.excludedBrowserLanguages.includes(code))) {
			errors.allowedBrowserLanguages = "A browser language cannot be both allowed and excluded.";
			errors.excludedBrowserLanguages = errors.allowedBrowserLanguages;
		}

		if (form.mode === "affiliate") {
			const partnerUids = String(form.partnerUids || "")
				.split(/[\s,]+/)
				.filter(Boolean);

			if (!partnerUids.length) {
				errors.partnerUids = "Enter at least one positive partner UID.";
			} else if (partnerUids.some((uid) => !/^\d+$/.test(uid) || !Number.isInteger(Number(uid)) || Number(uid) <= 0)) {
				errors.partnerUids = "Partner UIDs must be positive integers separated by commas or spaces.";
			}
		}

		form.steps.forEach((step, stepIndex) => {
			if (step.rewardType !== "freespins" && step.rewardType !== "both") return;

			const stepErrors = {};
			const gameId = Number(step.fsGameId);
			if (!Number.isInteger(gameId) || gameId < 1 || gameId > MAX_SIGNED_INT) {
				stepErrors.fsGameId = "Select a game with an ID from 1 to 2147483647.";
			}

			const betAmount = typeof step.fsBetAmount === "string" ? step.fsBetAmount : "";
			if (!/^\d+(?:\.\d{1,3})?$/.test(betAmount) || Number(betAmount) <= 0 || Number(betAmount) > 1000) {
				stepErrors.fsBetAmount = "Enter an amount greater than 0 and up to 1000, with at most 3 decimal places.";
			}

			if (!currenciesReady || !currencyOptions.some((option) => option.value === step.fsCurrency)) {
				stepErrors.fsCurrency = "Select an eligible Free Spins currency.";
			}

			const expirationDays = String(step.fsExpirationDays ?? "");
			if (!/^\d+$/.test(expirationDays) || Number(expirationDays) < 1 || Number(expirationDays) > 365) {
				stepErrors.fsExpirationDays = "Enter an integer from 1 to 365.";
			}

			if (!Array.isArray(step.fsParts) || step.fsParts.length === 0) {
				stepErrors.fsParts = "Add at least one Free Spins part.";
			} else {
				const partErrors = {};
				let totalSpins = 0;
				let previousDelay = null;

				step.fsParts.forEach((part, partIndex) => {
					const errorsForPart = {};
					const spinsCount = String(part.spinsCount ?? "");
					const delayHours = String(part.delayHours ?? "");
					const parsedSpins = Number(spinsCount);
					const parsedDelay = Number(delayHours);

					if (!/^\d+$/.test(spinsCount) || !Number.isInteger(parsedSpins) || parsedSpins < 1 || parsedSpins > 1000) {
						errorsForPart.spinsCount = "Enter an integer from 1 to 1000.";
					} else {
						totalSpins += parsedSpins;
					}

					if (
						!/^\d+$/.test(delayHours) ||
						!Number.isSafeInteger(parsedDelay) ||
						parsedDelay < MIN_FS_PART_DELAY_HOURS ||
						parsedDelay > MAX_FS_PART_DELAY_HOURS
					) {
						errorsForPart.delayHours = `Enter an integer from ${MIN_FS_PART_DELAY_HOURS} to ${MAX_FS_PART_DELAY_HOURS}.`;
						previousDelay = null;
					} else if (partIndex === 0 && parsedDelay !== 0) {
						errorsForPart.delayHours = "The first delay must be 0.";
						previousDelay = null;
					} else if (partIndex > 0 && previousDelay !== null && parsedDelay <= previousDelay) {
						errorsForPart.delayHours = "Delay offsets must be strictly increasing.";
						previousDelay = parsedDelay;
					} else {
						previousDelay = parsedDelay;
					}

					if (Object.keys(errorsForPart).length) partErrors[part._rowId] = errorsForPart;
				});

				if (totalSpins > 1000) stepErrors.fsParts = "Total spins across all parts must not exceed 1000.";
				if (Object.keys(partErrors).length) stepErrors.parts = partErrors;
			}

			if (Object.keys(stepErrors).length) errors[stepIndex] = stepErrors;
		});

		const errorFields = Object.keys(errors);
		setFieldErrors(errors);
		if (errorFields.length) {
			const hasGeneralErrors = errorFields.some((field) => !/^\d+$/.test(field));
			setActiveTabId(hasGeneralErrors ? "general" : "steps");
		}
		return errorFields.length === 0;
	};

	const save = () => {
		if (!countriesReady) return;
		if (!languagesReady) return;
		if (formHasFreespins && !currenciesReady) return;
		if (!validateForm()) return;

		const payload = {
			...form,
			allowedCountries: Array.isArray(form.allowedCountries) ? form.allowedCountries : [],
			claimPeriod: getClaimPeriodHours(form.claimPeriod, form.claimPeriodUnit),
			partnerUids:
				form.mode === "affiliate"
					? String(form.partnerUids || "")
							.split(/[\s,]+/)
							.filter(Boolean)
							.map(Number)
					: [],
			steps: form.steps.map((step) => {
				const rewardType = step.rewardType || "deposit";
				const hasFreespins = rewardType === "freespins" || rewardType === "both";

				return {
					...step,
					rewardType,
					bonusPercent: Number(step.bonusPercent),
					minDeposit: Number(step.minDeposit),
					maxBonusAmount: Number(step.maxBonusAmount),
					wager: Number(step.wager),
					cap: Number(step.cap),
					fsGameId: hasFreespins ? Number(step.fsGameId) : null,
					fsBetAmount: hasFreespins ? String(step.fsBetAmount) : null,
					fsCurrency: hasFreespins ? step.fsCurrency : null,
					fsExpirationDays: hasFreespins ? Number(step.fsExpirationDays) : null,
					fsParts: hasFreespins
						? step.fsParts.map((part) => ({
								spinsCount: Number(part.spinsCount),
								delayHours: Number(part.delayHours),
							}))
						: null,
				};
			}),
		};
		delete payload.claimPeriodUnit;
		patch({ loaded: false });
		Interface.request("set", { class: "data", module: "depositLadder", method: form.id ? "edit" : "add" }, payload)
			.then(() => {
				Interface.addNoty("Success", `Deposit ladder was ${form.id ? "updated" : "created"}`);
				close();
				Interface.loadDepositLadderList();
			})
			.catch((error) => {
				Interface.addNoty("Error", error?.text || error.toString());
				patch({ loaded: true });
			});
	};

	const setStepCount = (count) => {
		const next = Array.from({ length: count }, (_, index) => form.steps[index] || emptyStep(index + 1)).map((step, index) => ({ ...step, depositNumber: index + 1 }));
		setFieldErrors({});
		patch({ steps: next });
	};
	const updateStep = (index, value) => {
		setForm((current) => ({
			...current,
			steps: current.steps.map((step, stepIndex) => (stepIndex === index ? { ...step, ...value } : step)),
		}));
		setFieldErrors((current) => {
			const next = { ...current };
			delete next[index];
			return next;
		});
	};
	const setRewardType = (index, rewardType) => {
		const step = form.steps[index];
		if (rewardType === "deposit") {
			updateStep(index, {
				rewardType,
				fsGameId: null,
				fsBetAmount: null,
				fsCurrency: null,
				fsExpirationDays: null,
				fsParts: null,
			});
			return;
		}

		const freespinsFields = {
			rewardType,
			fsGameId: step.fsGameId ?? "",
			fsBetAmount: step.fsBetAmount ?? "",
			fsCurrency: step.fsCurrency ?? null,
			fsExpirationDays: step.fsExpirationDays ?? "",
			fsParts: Array.isArray(step.fsParts) && step.fsParts.length ? step.fsParts : [createFsPart(true)],
		};
		if (rewardType === "freespins") {
			Object.assign(freespinsFields, {
				bonusPercent: 0,
				maxBonusAmount: 0,
				wager: 0,
				cap: 0,
			});
		}
		updateStep(index, freespinsFields);
	};
	const updateFsPart = (stepIndex, rowId, value) => {
		const step = form.steps[stepIndex];
		updateStep(stepIndex, {
			fsParts: step.fsParts.map((part) => (part._rowId === rowId ? { ...part, ...value } : part)),
		});
	};
	const addFsPart = (stepIndex) => {
		const step = form.steps[stepIndex];
		const parts = Array.isArray(step.fsParts) ? step.fsParts : [];
		updateStep(stepIndex, { fsParts: [...parts, createFsPart(parts.length === 0)] });
	};
	const removeFsPart = (stepIndex, rowId) => {
		const step = form.steps[stepIndex];
		if (!Array.isArray(step.fsParts) || step.fsParts.length <= 1) return;

		const parts = step.fsParts.filter((part) => part._rowId !== rowId);
		const nextParts = parts.map((part, index) => (index === 0 ? { ...part, delayHours: "0" } : part));
		updateStep(stepIndex, { fsParts: nextParts });
	};

	const general = (
		<SpaceBetween size="m">
			<FormField label="Internal name">
				<Input value={form.name} onChange={({ detail }) => patch({ name: detail.value })} />
			</FormField>
			<TextField label="Title" field="title" typeField="titleType" form={form} patch={patch} />
			<TextField label="Subtitle" field="subtitle" typeField="subtitleType" form={form} patch={patch} />
			<TextField label="Button text" field="buttonText" typeField="buttonTextType" form={form} patch={patch} />
			<Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
				{numberField("Claim period", "claimPeriod")}
				<FormField label="Claim period unit">
					<Select
						selectedOption={periodOptions.find((item) => item.value === form.claimPeriodUnit)}
						options={periodOptions}
						onChange={({ detail }) => {
							const nextUnit = detail.selectedOption.value;
							const hours = getClaimPeriodHours(form.claimPeriod, form.claimPeriodUnit);

							patch({
								claimPeriod: nextUnit === "days" ? hours / 24 : hours,
								claimPeriodUnit: nextUnit,
							});
						}}
					/>
				</FormField>
				<FormField label="Mode">
					<Select selectedOption={modeOptions.find((item) => item.value === form.mode)} options={modeOptions} onChange={({ detail }) => patch({ mode: detail.selectedOption.value })} />
				</FormField>
			</Grid>
			{form.mode === "affiliate" ? (
				<FormField label="Partner UIDs" description="comma/space separated positive partner UIDs." errorText={fieldErrors.partnerUids}>
					<Input
						value={form.partnerUids}
						onChange={({ detail }) => {
							patch({ partnerUids: detail.value });
							setFieldErrors((current) => {
								const next = { ...current };
								delete next.partnerUids;
								return next;
							});
						}}
					/>
				</FormField>
			) : null}
			{countriesError ? (
				<Alert type="error" action={<Button onClick={loadCountries}>Retry</Button>}>
					Allowed countries could not be loaded.
				</Alert>
			) : null}
			<FormField label="Allowed countries" description="Leave empty for unrestricted.">
				<Multiselect
					filteringType="auto"
					selectedOptions={(Array.isArray(form.allowedCountries) ? form.allowedCountries : []).map(
						(code) => countryOptions.find((option) => option.value === code) || { value: code, label: code },
					)}
					options={countryOptions}
					statusType={countriesLoading ? "loading" : "finished"}
					loadingText="Loading allowed countries"
					empty="No allowed countries"
					placeholder="All countries"
					selectedAriaLabel="Selected"
					onChange={({ detail }) => patch({ allowedCountries: detail.selectedOptions.map((option) => option.value) })}
					disabled={!countriesReady}
				/>
			</FormField>
			{languagesError ? (
				<Alert type="error" action={<Button onClick={loadLanguages}>Retry</Button>}>
					Browser languages could not be loaded.
				</Alert>
			) : null}
			{[
				{ field: "allowedBrowserLanguages", label: "Browser language equals (=)", placeholder: "All browser languages" },
				{ field: "excludedBrowserLanguages", label: "Browser language does not equal (!=)", placeholder: "No excluded languages" },
			].map(({ field, label, placeholder }) => (
				<FormField key={field} label={label} errorText={fieldErrors[field]} description="Checked only at first assignment, using the browser's primary language without the region. Leave empty for unrestricted. Existing assignments are retained.">
					<Multiselect
						filteringType="auto"
						selectedOptions={(Array.isArray(form[field]) ? form[field] : []).map((code) => languageOptions.find((option) => option.value === code) || { value: code, label: code })}
						options={languageOptions}
						statusType={languagesLoading ? "loading" : "finished"}
						loadingText="Loading browser languages"
						empty="No browser languages"
						placeholder={placeholder}
						selectedAriaLabel="Selected"
						onChange={({ detail }) => {
							patch({ [field]: detail.selectedOptions.map((option) => option.value) });
							setFieldErrors((current) => ({ ...current, allowedBrowserLanguages: undefined, excludedBrowserLanguages: undefined }));
						}}
						disabled={!languagesReady}
					/>
				</FormField>
			))}
			<Toggle checked={Boolean(Number(form.active))} onChange={({ detail }) => patch({ active: detail.checked ? 1 : 0 })}>
				{Number(form.active) ? "Active" : "Inactive"}
			</Toggle>
		</SpaceBetween>
	);

	const steps = (
		<SpaceBetween size="m">
			{gamesError ? (
				<Alert type="error" action={<Button onClick={loadGames}>Retry</Button>}>
					Eligible Free Spins games could not be loaded.
				</Alert>
			) : null}
			{currenciesError ? (
				<Alert type="error" action={<Button onClick={loadCurrencies}>Retry</Button>}>
					Eligible Free Spins currencies could not be loaded.
				</Alert>
			) : null}
			<FormField label="Steps count">
				<Select disabled={stepsReadOnly} selectedOption={{ value: String(form.steps.length), label: String(form.steps.length) }} options={[1, 2, 3, 4, 5].map((value) => ({ value: String(value), label: String(value) }))} onChange={({ detail }) => setStepCount(Number(detail.selectedOption.value))} />
			</FormField>
			{form.steps.map((step, index) => {
				const rewardType = step.rewardType || "deposit";
				const hasDepositBonus = rewardType === "deposit" || rewardType === "both";
				const hasFreespins = rewardType === "freespins" || rewardType === "both";
				const stepError = fieldErrors[index] || {};
				const selectedGame =
					gameOptions.find((option) => option.value === String(step.fsGameId)) ||
					(step.fsGameId !== null && typeof step.fsGameId !== "undefined" && step.fsGameId !== ""
						? { value: String(step.fsGameId), label: `Game #${step.fsGameId}` }
						: null);
				const selectedCurrency = currencyOptions.find((option) => option.value === step.fsCurrency) || null;

				return (
					<Container key={step.depositNumber} header={<Header variant="h3">Deposit {step.depositNumber}</Header>}>
						<SpaceBetween size="m">
							<FormField label="Reward type">
								<Select
									selectedOption={rewardTypeOptions.find((option) => option.value === rewardType)}
									options={rewardTypeOptions}
									onChange={({ detail }) => setRewardType(index, detail.selectedOption.value)}
									disabled={stepsReadOnly}
								/>
							</FormField>
							{hasDepositBonus ? (
								<>
									<Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
										<FormField label="Bonus percent">
											<Input disabled={stepsReadOnly} type="number" value={String(step.bonusPercent ?? "")} onChange={({ detail }) => updateStep(index, { bonusPercent: detail.value })} />
										</FormField>
										<FormField label="Min deposit, RUB">
											<Input type="number" value={String(step.minDeposit ?? "")} onChange={({ detail }) => updateStep(index, { minDeposit: detail.value })} />
										</FormField>
										<FormField label="Max bonus amount, RUB">
											<Input disabled={stepsReadOnly} type="number" value={String(step.maxBonusAmount ?? "")} onChange={({ detail }) => updateStep(index, { maxBonusAmount: detail.value })} />
										</FormField>
									</Grid>
									<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
										<FormField label="Wager">
											<Input type="number" value={String(step.wager ?? "")} onChange={({ detail }) => updateStep(index, { wager: detail.value })} />
										</FormField>
										<FormField label="Cap">
											<Input type="number" value={String(step.cap ?? "")} onChange={({ detail }) => updateStep(index, { cap: detail.value })} />
										</FormField>
									</Grid>
								</>
							) : (
								<FormField label="Min deposit, RUB">
									<Input type="number" value={String(step.minDeposit ?? "")} onChange={({ detail }) => updateStep(index, { minDeposit: detail.value })} />
								</FormField>
							)}
							{hasFreespins ? (
								<SpaceBetween size="m">
									<Header variant="h3">Free Spins</Header>
									<Grid
										gridDefinition={[
											{ colspan: { default: 12, xxs: 3 } },
											{ colspan: { default: 12, xxs: 3 } },
											{ colspan: { default: 12, xxs: 3 } },
											{ colspan: { default: 12, xxs: 3 } },
										]}
									>
										<FormField label="Game" errorText={stepError.fsGameId}>
											<Select
												filteringType="auto"
												selectedOption={selectedGame}
												options={gameOptions}
												statusType={gamesLoading ? "loading" : "finished"}
												loadingText="Loading eligible Free Spins games"
												empty="No eligible Free Spins games"
												placeholder="Select one game"
												onChange={({ detail }) => updateStep(index, { fsGameId: detail.selectedOption.value })}
												disabled={stepsReadOnly || gamesError}
											/>
										</FormField>
										<FormField label="Bet amount" errorText={stepError.fsBetAmount}>
											<Input
												value={String(step.fsBetAmount ?? "")}
												inputMode="decimal"
												onChange={({ detail }) => updateStep(index, { fsBetAmount: detail.value })}
												disabled={stepsReadOnly}
											/>
										</FormField>
										<FormField label="Bet currency" errorText={stepError.fsCurrency}>
											<Select
												filteringType="auto"
												selectedOption={selectedCurrency}
												options={currencyOptions}
												statusType={currenciesLoading ? "loading" : "finished"}
												loadingText="Loading eligible Free Spins currencies"
												empty="No eligible Free Spins currencies"
												placeholder="Select currency"
												onChange={({ detail }) => updateStep(index, { fsCurrency: detail.selectedOption.value })}
												disabled={stepsReadOnly || !currenciesReady}
											/>
										</FormField>
										<FormField label="Expiration days" constraintText="Allowed range: 1–365 days." errorText={stepError.fsExpirationDays}>
											<Input
												value={String(step.fsExpirationDays ?? "")}
												inputMode="numeric"
												onChange={({ detail }) => updateStep(index, { fsExpirationDays: detail.value })}
												disabled={stepsReadOnly}
											/>
										</FormField>
									</Grid>
									<FormField label="Free Spins parts" errorText={stepError.fsParts}>
										<SpaceBetween size="s">
											{step.fsParts.map((part, partIndex) => {
												const partError = stepError.parts?.[part._rowId] || {};
												const canRemovePart = step.fsParts.length > 1;

												return (
													<Grid
														key={part._rowId}
														gridDefinition={
															canRemovePart
																? [
																		{ colspan: { default: 12, xxs: 5 } },
																		{ colspan: { default: 12, xxs: 5 } },
																		{ colspan: { default: 12, xxs: 2 } },
																	]
																: [
																		{ colspan: { default: 12, xxs: 6 } },
																		{ colspan: { default: 12, xxs: 6 } },
																	]
														}
													>
														<FormField label={`Part ${partIndex + 1} spins`} errorText={partError.spinsCount}>
															<Input
																value={String(part.spinsCount ?? "")}
																inputMode="numeric"
																onChange={({ detail }) => updateFsPart(index, part._rowId, { spinsCount: detail.value })}
																disabled={stepsReadOnly}
															/>
														</FormField>
														<FormField label="Delay hours" errorText={partError.delayHours}>
															<Input
																type="number"
																value={String(part.delayHours ?? "")}
																inputMode="numeric"
																step={1}
																onChange={({ detail }) => updateFsPart(index, part._rowId, { delayHours: detail.value })}
																disabled={stepsReadOnly || partIndex === 0}
															/>
														</FormField>
														{canRemovePart ? (
															<Box padding={{ top: "l" }}>
																<Button wrapText={false} onClick={() => removeFsPart(index, part._rowId)} disabled={stepsReadOnly}>
																	Remove
																</Button>
															</Box>
														) : null}
													</Grid>
												);
											})}
											<Button iconName="add-plus" onClick={() => addFsPart(index)} disabled={stepsReadOnly}>
												Add part
											</Button>
										</SpaceBetween>
									</FormField>
								</SpaceBetween>
							) : null}
						</SpaceBetween>
					</Container>
				);
			})}
		</SpaceBetween>
	);

	return (
		<Modal
			visible={form.show}
			size="large"
			header={form.formMode === "edit" ? "Edit deposit ladder" : "Add deposit ladder"}
			onDismiss={close}
			footer={
				<Box float="right">
					<SpaceBetween direction="horizontal" size="s">
						<Button onClick={close}>Close</Button>
						<Button variant="primary" disabled={!form.loaded || !countriesReady || !languagesReady || (formHasFreespins && !currenciesReady)} loading={!form.loaded} onClick={save}>
							Save
						</Button>
					</SpaceBetween>
				</Box>
			}>
			{form.loaded ? (
				<Tabs
					activeTabId={activeTabId}
					onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
					tabs={[
						{ id: "general", label: "General", content: general },
						{ id: "steps", label: "Steps", content: steps },
					]}
				/>
			) : (
				<Box padding={{ top: "xxxl", bottom: "xxxl" }} textAlign="center">
					<Spinner size="large" />
				</Box>
			)}
		</Modal>
	);
}
